import { useAuth0 } from '@auth0/auth0-react';
import { Outlet } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { sessionAuthService } from '@/services/session-auth-service';
import { isHandlingSessionExpiration } from '@/utils/api-client';
import { 
  FullPageLoader, 
  Alert, 
  AlertTitle, 
  AlertDescription, 
  Card, 
  CardContent, 
  Button, 
  Header,
  ContentContainer
} from 'prosci-ui';
import { AlertCircle } from '@/lib/icons';

export function ProtectedRoute() {
  const { isAuthenticated, isLoading, loginWithRedirect, error, user } = useAuth0();
  const [hasSession, setHasSession] = useState(() => sessionAuthService.isAuthenticated());

  // Listen for session changes
  useEffect(() => {
    const handleSessionChange = () => {
      const sessionExists = sessionAuthService.isAuthenticated();
      setHasSession(sessionExists);
    };

    sessionAuthService.addEventListener('sessionChanged', handleSessionChange);

    return () => {
      sessionAuthService.removeEventListener('sessionChanged', handleSessionChange);
    };
  }, []);

  // Re-check session on each render
  useEffect(() => {
    const sessionExists = sessionAuthService.isAuthenticated();
    setHasSession(sessionExists);
  });

  useEffect(() => {
    // Don't redirect if we're handling a session expiration (401 error)
    if (isHandlingSessionExpiration()) {
      console.log('[ProtectedRoute] Skipping Auth0 redirect - session expiration is being handled');
      return;
    }

    // Don't redirect if we're processing a callback (URL has code or error params)
    const searchParams = new URLSearchParams(window.location.search);
    const hasAuthParams = searchParams.has('code') || searchParams.has('error') || searchParams.has('state');
    const hasJWT = searchParams.has('jwt') || searchParams.has('token') || searchParams.has('access');

    // Don't redirect to Auth0 if we have a JWT (license admin login) or existing session
    if (!isLoading && !isAuthenticated && !hasAuthParams && !hasJWT && !hasSession && !error) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname }
      });
    }
  }, [isAuthenticated, isLoading, loginWithRedirect, error, user, hasSession]);

  if (isLoading) {
    return <FullPageLoader message="Loading..." />;
  }

  if (error) {
    return (
      <main className="min-h-screen bg-neutral-50">
        <Header
          title="Authentication Error"
          description={error.message}
          size="small"
        />
        <ContentContainer>
          <Card size="medium">
            <CardContent>
              {error.message === 'Unauthorized' && (
                <Alert variant="warning">
                  <AlertTitle>Common Causes</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc list-inside space-y-1 mt-2">
                      <li>The Auth0 audience is not configured correctly</li>
                      <li>The user doesn't have access to this application</li>
                      <li>The Auth0 application is not properly configured</li>
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="flex justify-center pt-2">
                <Button onClick={() => window.location.href = '/'}>
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </ContentContainer>
      </main>
    );
  }

  if (!isAuthenticated && !hasSession) {
    return null; // Return null while redirecting to Auth0
  }

  return <Outlet />;
}